// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Realmfold — the game's own sound cues, and the list of every cue name it will ever play.
//
// WHY THIS FILE EXISTS: `sfx.play()` on an unknown cue is a deliberate silent no-op in the engine
// (`const p = patches[name]; if (!p) return;`), which is right for a mixer and means A CUE-NAME TYPO
// IS PERMANENTLY INVISIBLE. It cannot throw, jsdom cannot hear it, and a screenshot cannot show it —
// a sibling game shipped its signature moment silent for exactly this reason. So the custom patches
// live here, CUES lists every name the game plays, and tests/source-hygiene.test.ts asserts both
// halves: every name in CUES is known to a real Sfx, and every play() literal in src/ is in CUES.

import type { Patch } from '@ben-gy/game-engine/sound';

/**
 * Wood, stone and water. A tile-laying game wants weight rather than sparkle, so nothing here is a
 * bright arcade blip — the one bright sound in the set is the coronet, which is the only moment that
 * should feel like a prize.
 */
export const PATCHES: Record<string, Patch> = {
  /** An offer row lifted off the table. Short, dry, low. */
  lift: { type: 'triangle', freq: [330, 250], dur: 0.07, gain: 0.16 },
  /** A doublet claimed. Pitched by its number at the call site, so a fat pick sounds expensive. */
  claim: { type: 'triangle', freq: [420, 620], dur: 0.11, gain: 0.2 },
  /** The doublet coming down on the demesne. The most-heard sound in the game, so the quietest. */
  settle: { type: 'sine', freq: [220, 130], dur: 0.1, gain: 0.18 },
  /** The placement joined a region — a rising third, pitched up the merge. */
  merge: { type: 'triangle', freq: [420, 700], dur: 0.16, gain: 0.2 },
  /** A coroneted cell landing. The prize. */
  coronet: { type: 'square', freq: [880, 1320], dur: 0.15, gain: 0.15 },
  /** A doublet with nowhere to go. Falls away rather than rising. */
  spoil: { type: 'sawtooth', freq: [280, 90], dur: 0.24, gain: 0.2, noise: true },
  /** holt: a doublet struck out of the match. */
  burn: { type: 'sawtooth', freq: [520, 70], dur: 0.3, gain: 0.24, noise: true },
};

/**
 * EVERY cue name the game plays, engine built-in or custom. Keep it in sync with the source — the
 * test greps the call sites and fails if a literal is missing from this list.
 */
export const CUES: readonly string[] = [
  // custom
  'lift',
  'claim',
  'settle',
  'merge',
  'coronet',
  'spoil',
  'burn',
  // engine built-ins the game uses
  'blip',
  'select',
  'win',
  'lose',
  'beat',
  'go',
];
